
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { exportToWordWithErrorHandling as exportToWord, exportToSheetsWithErrorHandling as exportToSheets } from "@/utils/exports";
import { Node, Edge } from "@xyflow/react";
import { FileText, FileSpreadsheet, Copy, X } from "lucide-react";

interface ShareWhiteboardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  nodes: Node[];
  edges: Edge[];
  currentWhiteboardId?: string | null;
  whiteboardName?: string;
}

const ShareWhiteboard: React.FC<ShareWhiteboardProps> = ({
  open,
  onOpenChange,
  nodes,
  edges,
  whiteboardName = "My Whiteboard",
}) => {
  const [exportName, setExportName] = useState(whiteboardName);
  const [activeTab, setActiveTab] = useState("word");

  const handleExport = () => {
    if (activeTab === "word") {
      exportToWord(nodes, edges, exportName);
    } else {
      exportToSheets(nodes, edges, exportName);
    }
    onOpenChange(false);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center">
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => onOpenChange(false)}></div>
      <div className="relative bg-white rounded-lg shadow-lg max-w-md w-full mx-4 max-h-[90vh] overflow-hidden">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold">Share Whiteboard</h2>
              <p className="text-sm text-gray-600 mt-1">
                Export your whiteboard to Microsoft Word or Google Sheets format.
                The node positions will be locked but text content will be editable.
              </p>
            </div>
            <button
              onClick={() => onOpenChange(false)}
              className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
            >
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </button>
          </div>

          {/* Custom Tabs Implementation */}
          <div className="w-full">
            <div className="grid grid-cols-2 mb-4 bg-gray-100 rounded-md p-1">
              <button
                className={`flex items-center gap-2 justify-center py-2 px-3 rounded transition-colors ${
                  activeTab === "word" 
                    ? "bg-white shadow-sm" 
                    : "hover:bg-gray-200"
                }`}
                onClick={() => setActiveTab("word")}
              >
                <FileText size={16} />
                <span>Word</span>
              </button>
              <button
                className={`flex items-center gap-2 justify-center py-2 px-3 rounded transition-colors ${
                  activeTab === "sheets" 
                    ? "bg-white shadow-sm" 
                    : "hover:bg-gray-200"
                }`}
                onClick={() => setActiveTab("sheets")}
              >
                <FileSpreadsheet size={16} />
                <span>Google Sheets</span>
              </button>
            </div>
            
            {activeTab === "word" && (
              <div className="space-y-4 py-2">
                <div className="space-y-2">
                  <Label htmlFor="export-name">Export name</Label>
                  <Input 
                    id="export-name" 
                    value={exportName} 
                    onChange={(e) => setExportName(e.target.value)}
                    placeholder="Enter a name for your export"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Export Details</Label>
                  <div className="text-sm text-gray-600">
                    <p>This will export your whiteboard as an HTML file that can be opened in Microsoft Word.</p>
                    <ul className="list-disc pl-4 mt-2">
                      <li>Node positions and connections will be preserved</li>
                      <li>Text content will be editable</li>
                      <li>Colors and categories will be included</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}
            
            {activeTab === "sheets" && (
              <div className="space-y-4 py-2">
                <div className="space-y-2">
                  <Label htmlFor="export-name-sheets">Export name</Label>
                  <Input 
                    id="export-name-sheets" 
                    value={exportName} 
                    onChange={(e) => setExportName(e.target.value)}
                    placeholder="Enter a name for your export"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Export Details</Label>
                  <div className="text-sm text-gray-600">
                    <p>This will export your whiteboard as a CSV file that can be imported into Google Sheets.</p>
                    <ul className="list-disc pl-4 mt-2">
                      <li>Each node will be represented as a row</li>
                      <li>Node connections will be preserved as references</li>
                      <li>Text content can be edited after import</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-start gap-2 mt-6">
            <Button
              type="button"
              onClick={handleExport}
              className="flex items-center gap-2"
            >
              <Copy size={16} />
              Export
            </Button>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareWhiteboard;
